"use strict";
/**
 * Mock data for the Personas admin view.
 *
 * The groups and personas below mirror the cross-use-case persona
 * pattern described in `docs/use-case-element-popups-*.md`: every
 * mention of a "Persona" element across the use-case Markdown is
 * normalised into a single canonical record (one row per real-world
 * role) and rolled up by business function. Personas are intentionally
 * close-but-not-identical variants of the source roles so the admin can
 * see how the same role surfaced from multiple use cases collapses into
 * a single bucket.
 *
 * TODO: Replace with real data fetched from the elements / personas
 * service once the canonical persona store endpoints are available.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PERSONAS_MOCK_SCHEMA = void 0;
var mockUseCase = function (id, title) { return ({
    id: id,
    title: title,
}); };
// Travel-domain use cases reused across multiple persona groups, kept
// in sync with the Metrics mock so personas and metrics tell the same
// story across admin tabs.
var UC_TRAVEL_AGENT = mockUseCase('uc-101', 'Avanade Travel Agent Assistant');
var UC_TRAVEL_COPILOT = mockUseCase('uc-102', 'Corporate Travel Booking Co-pilot');
var UC_EXPENSE = mockUseCase('uc-103', 'Expense Report Auto-classification');
var UC_DISRUPTION = mockUseCase('uc-104', 'Trip Disruption Recovery');
var UC_POLICY = mockUseCase('uc-105', 'Travel Policy Compliance Auditor');
var UC_LOYALTY = mockUseCase('uc-106', 'Loyalty Program Reconciliation');
var UC_FINOPS = mockUseCase('uc-107', 'Finance Operations Insights');
exports.PERSONAS_MOCK_SCHEMA = [
    {
        id: 'finance',
        name: 'Finance',
        kind: 'predefined',
        properties: [
            {
                id: 'finance-manager',
                name: 'Finance Manager',
                internalName: 'finance_manager',
                system: 'Finance',
                useCases: [UC_FINOPS, UC_EXPENSE, UC_POLICY],
            },
            {
                id: 'fpa-analyst',
                name: 'FP&A Analyst',
                internalName: 'fpa_analyst',
                system: 'Finance',
                useCases: [UC_FINOPS, UC_EXPENSE],
            },
            {
                id: 'accounts-payable-clerk',
                name: 'Accounts Payable Clerk',
                internalName: 'ap_clerk',
                system: 'Finance',
                useCases: [UC_EXPENSE],
            },
            {
                id: 'controller',
                name: 'Controller',
                internalName: 'controller',
                system: 'Finance',
                useCases: [UC_FINOPS],
            },
        ],
    },
    {
        id: 'travel-and-operations',
        name: 'Travel & Operations',
        kind: 'predefined',
        properties: [
            {
                id: 'corporate-traveler',
                name: 'Corporate Traveler',
                internalName: 'corporate_traveler',
                system: 'Operations',
                useCases: [UC_TRAVEL_AGENT, UC_TRAVEL_COPILOT, UC_DISRUPTION],
            },
            {
                id: 'travel-coordinator',
                name: 'Travel Coordinator',
                internalName: 'travel_coordinator',
                system: 'Operations',
                useCases: [UC_TRAVEL_COPILOT, UC_DISRUPTION, UC_POLICY],
            },
            {
                id: 'travel-ops-manager',
                name: 'Travel Operations Manager',
                internalName: 'travel_ops_manager',
                system: 'Operations',
                useCases: [UC_TRAVEL_AGENT, UC_DISRUPTION],
            },
            {
                id: 'executive-assistant',
                name: 'Executive Assistant',
                internalName: 'executive_assistant',
                system: 'Operations',
                useCases: [UC_TRAVEL_COPILOT],
            },
        ],
    },
    {
        id: 'hr-and-people',
        name: 'HR & People',
        kind: 'predefined',
        properties: [
            {
                id: 'hr-business-partner',
                name: 'HR Business Partner',
                internalName: 'hr_business_partner',
                system: 'Human Resources',
                useCases: [UC_TRAVEL_AGENT],
            },
            {
                id: 'people-analytics-lead',
                name: 'People Analytics Lead',
                internalName: 'people_analytics_lead',
                system: 'Human Resources',
                useCases: [],
            },
            {
                id: 'employee-experience-manager',
                name: 'Employee Experience Manager',
                internalName: 'employee_experience_manager',
                system: 'Human Resources',
                useCases: [UC_TRAVEL_AGENT, UC_DISRUPTION],
            },
        ],
    },
    {
        id: 'sales-and-account-management',
        name: 'Sales & Account Management',
        kind: 'predefined',
        properties: [
            {
                id: 'account-executive',
                name: 'Account Executive',
                internalName: 'account_executive',
                system: 'Sales',
                useCases: [UC_TRAVEL_AGENT, UC_TRAVEL_COPILOT],
            },
            {
                id: 'sales-operations-analyst',
                name: 'Sales Operations Analyst',
                internalName: 'sales_operations_analyst',
                system: 'Sales',
                useCases: [UC_TRAVEL_COPILOT, UC_FINOPS],
            },
            {
                id: 'pre-sales-engineer',
                name: 'Pre-Sales Engineer',
                internalName: 'pre_sales_engineer',
                system: 'Sales',
                useCases: [UC_TRAVEL_AGENT],
            },
        ],
    },
    {
        id: 'it-and-engineering',
        name: 'IT & Engineering',
        kind: 'ai-generated',
        properties: [
            {
                id: 'platform-engineer',
                name: 'Platform Engineer',
                internalName: 'platform_engineer',
                system: 'IT',
                useCases: [UC_TRAVEL_AGENT, UC_TRAVEL_COPILOT],
            },
            {
                id: 'identity-administrator',
                name: 'Identity Administrator',
                internalName: 'identity_administrator',
                system: 'IT',
                useCases: [UC_TRAVEL_AGENT, UC_POLICY],
            },
            {
                id: 'integration-engineer',
                name: 'Integration Engineer',
                internalName: 'integration_engineer',
                system: 'IT',
                useCases: [UC_LOYALTY, UC_FINOPS],
            },
            {
                id: 'site-reliability-engineer',
                name: 'Site Reliability Engineer',
                internalName: 'site_reliability_engineer',
                system: 'IT',
                useCases: [UC_DISRUPTION],
            },
        ],
    },
    {
        id: 'customer-service',
        name: 'Customer Service',
        kind: 'predefined',
        properties: [
            {
                id: 'service-desk-agent',
                name: 'Service Desk Agent',
                internalName: 'service_desk_agent',
                system: 'Service Desk',
                useCases: [UC_TRAVEL_AGENT, UC_DISRUPTION],
            },
            {
                id: 'customer-success-manager',
                name: 'Customer Success Manager',
                internalName: 'customer_success_manager',
                system: 'Service Desk',
                useCases: [UC_TRAVEL_COPILOT],
            },
            {
                id: 'escalation-lead',
                name: 'Escalation Lead',
                internalName: 'escalation_lead',
                system: 'Service Desk',
                useCases: [UC_DISRUPTION],
            },
        ],
    },
    {
        id: 'compliance-and-risk',
        name: 'Compliance & Risk',
        kind: 'ai-generated',
        properties: [
            {
                id: 'compliance-officer',
                name: 'Compliance Officer',
                internalName: 'compliance_officer',
                system: 'Compliance',
                useCases: [UC_POLICY, UC_FINOPS],
            },
            {
                id: 'risk-analyst',
                name: 'Risk Analyst',
                internalName: 'risk_analyst',
                system: 'Compliance',
                useCases: [UC_POLICY],
            },
            {
                id: 'audit-lead',
                name: 'Audit Lead',
                internalName: 'audit_lead',
                system: 'Compliance',
                useCases: [UC_POLICY, UC_FINOPS],
            },
            {
                id: 'data-protection-officer',
                name: 'Data Protection Officer',
                internalName: 'data_protection_officer',
                system: 'Compliance',
                useCases: [UC_POLICY],
            },
        ],
    },
];
